const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/components/ConfirmationDialog/ConfirmationDialog.tsx';
import * as React from 'react';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { DialogContent } from '@mui/material';
import { TextInput, useOptionalText } from '../../utils/intlUtils';
import { KnopfType } from '../Knopf/Knopf';
import {
    RESPONSIVE_DIALOG_DEFAULT_MIN_MARGIN,
    ResponsiveDialog,
    ResponsiveDialogSizeSettings,
} from '../ResponsiveDialog/ResponsiveDialog';
import { ActionType } from '@cistec/common_web/components/defs';
import { ConfirmationDialogActions } from '@cistec/common_web/components/ConfirmationDialog/ConfirmationDialogActions';
import { ConfirmationDialogCloseHandler } from '@cistec/common_web/components/ConfirmationDialog/defs';
import { useTheme } from '@mui/material/styles';
import { css, cx } from '@emotion/css';

function useCSSClasses() {
    const theme = useTheme();
    return useMemo(
        () => css`
            &.fullScreen .ConfirmationDialogActions.Footer.secondary {
                height: ${theme.spacing(10)};
                padding: ${theme.spacing(1)} ${theme.spacing(3)} 0;
            }
        `,
        [theme]
    );
}

export interface Props {
    allowClosing?: boolean;
    cancelText?: TextInput;
    children?: ReactNode;
    className?: string;
    confirmActionType?: ActionType;
    confirmButtonType?: KnopfType;
    confirmDisabled?: boolean;
    confirmText?: TextInput;
    dynamicFullscreen?: boolean;
    backdropBlur?: boolean;
    onClose?: ConfirmationDialogCloseHandler;
    open?: boolean;
    sizeSettings?: Partial<ResponsiveDialogSizeSettings>;
    text?: TextInput;
    title?: TextInput;
}

export const ConfirmationDialog = (props: Props) => {
    const { children, dynamicFullscreen = false, backdropBlur = false } = props;

    const text = useOptionalText(props.text);

    const className = useCSSClasses();

    const openProp = props.open ?? true; // Not ideal, but necessary for now until ResponsiveDialog is no longer in App.tsx / is without action

    const [isOpen, setIsOpen] = useState(openProp);

    useEffect(() => {
        setIsOpen(openProp);
    }, [openProp]);

    const close = (confirmed?: boolean) => {
        setIsOpen(false);
        props.onClose?.(confirmed);
    };

    const handleResponsiveDialogClose = () => {
        close();
    };

    const handleConfirmationDialogActionsClose = (confirmed: boolean) => {
        close(confirmed);
    };

    const renderDefaultContent = () => {
        return (
            <>
                {text ? <DialogContent style={{ padding: 16 }}>{text}</DialogContent> : null}
                <ConfirmationDialogActions
                    cancelText={props.cancelText}
                    confirmActionType={props.confirmActionType}
                    confirmButtonType={props.confirmButtonType}
                    confirmDisabled={props.confirmDisabled}
                    confirmText={props.confirmText}
                    onClose={handleConfirmationDialogActionsClose}
                />
            </>
        );
    };

    const content = children ? children : renderDefaultContent();

    const width = props.sizeSettings?.width;
    const minMargin = props.sizeSettings?.minMargin ?? RESPONSIVE_DIALOG_DEFAULT_MIN_MARGIN;
    const applyQuery = dynamicFullscreen && width;
    const query = applyQuery ? `@media (max-width: calc(${width} + ${minMargin * 2}px))` : undefined;

    return (
        <ResponsiveDialog
            allowClosing={props.allowClosing}
            className={cx(className, props.className)}
            open={isOpen}
            fullScreen={applyQuery ? query : false}
            onClose={handleResponsiveDialogClose}
            sizeSettings={props.sizeSettings}
            backdropBlur={backdropBlur}
            title={props.title}
        >
            {content}
        </ResponsiveDialog>
    );
};
